import { useEffect, useState, useRef, useCallback } from 'react'
import AudioPitchShifter from '../utils/AudioPitchShifter'
import AudioCache from '../utils/AudioCache'
import {
  getSongMediaKey,
  getVocalTrackUrl,
  getAccompanimentTrackUrl,
  getDrumsTrackUrl,
  getMainAudioUrl,
} from '../lib/songMedia'

const EngineStatus = {
  IDLE: 'idle',
  LOADING: 'loading',
  READY: 'ready',
  SUSPENDED: 'suspended', // Waiting for user gesture
  ERROR: 'error'
};

const SONG_ENDED_DEDUPE_MS = 1500

const useAudioEngine = (
  currentSong,
  pitchShift,
  vocalVolume,
  accompanimentVolume,
  vocalMuted,
  accompanimentMuted,
  drumsVolume = 0.8,
  drumsMuted = false,
  playbackVersion = 0,
  isPlaying = false,
  authoritativeAnchorPosSec = null,
  authoritativeAnchorServerMs = null,
  authoritativeRevision = null,
  authorityMode = 'follower'  // 'authority' | 'follower'
) => {
  const pitchShifterRef = useRef(null)
  const audioCacheRef = useRef(null)

  // Consolidated Engine State
  const [status, setStatus] = useState(EngineStatus.IDLE)
  const [errorMessage, setErrorMessage] = useState(null)

  const [currentTime, setCurrentTime] = useState(0)
  const [localIsPlaying, setLocalIsPlaying] = useState(false)

  const songEndedCallbackRef = useRef(null)
  const lastSongEndedSignalAtRef = useRef(0)
  const backupTimerRef = useRef(null)
  const currentPlaybackVersionRef = useRef(playbackVersion)
  const loadedSongRef = useRef({ mediaKey: null, version: -1 })
  const initialAuthoritySyncDoneRef = useRef(-1)

  // UI state for legacy compatibility or granular progress (if needed)
  // We derive these for ease of transition in Step 1
  const audioLoading = status === EngineStatus.LOADING
  const audioTracksLoaded = status === EngineStatus.READY
  const needsUserGesture = status === EngineStatus.SUSPENDED
  const audioError = status === EngineStatus.ERROR ? errorMessage : null

  if (currentPlaybackVersionRef.current !== playbackVersion) {
    currentPlaybackVersionRef.current = playbackVersion
    if (localIsPlaying) {
      console.log(`🎵 🔄 Session change detected (${playbackVersion}), resetting localIsPlaying`)
      setLocalIsPlaying(false)
    }
  }

  const lyricsReadyCallbackRef = useRef(null)

  const invokeSongEndedCallback = useCallback(() => {
    if (!songEndedCallbackRef.current) return

    const now = Date.now()
    const elapsedMs = now - lastSongEndedSignalAtRef.current
    if (elapsedMs >= 0 && elapsedMs < SONG_ENDED_DEDUPE_MS) {
      console.log(`🎵 Duplicate song-ended signal ignored (${elapsedMs}ms)`)
      return
    }

    lastSongEndedSignalAtRef.current = now
    songEndedCallbackRef.current()
  }, [])

  // Initialize audio engine primitives
  useEffect(() => {
    let disposed = false

    const initializePrimitives = async () => {
      try {
        console.log('🎵 Preparing Audio Primitives...')
        const pitchShifter = new AudioPitchShifter()
        const success = await pitchShifter.initialize()

        if (disposed) {
          pitchShifter.destroy()
          return
        }

        if (success) {
          pitchShifterRef.current = pitchShifter

          // Initialize audio cache
          const audioCache = new AudioCache(3)
          audioCache.setEventCallbacks({
            onCacheHit: (songId) => console.log(`🎵 📦 Cache hit for ${songId}`),
            onCacheMiss: (songId) => console.log(`🎵 📦 Cache miss for ${songId}`),
          })
          if (disposed) {
            audioCache.clear()
            pitchShifter.destroy()
            return
          }
          audioCacheRef.current = audioCache

          // Set up time tracking callback
          pitchShifter.setTimeUpdateCallback((currentTime) => {
            setCurrentTime(currentTime)
          })

          console.log('🎵 Audio Primitives Ready.')
          // We stay in IDLE until a song triggers a load
        } else {
          setStatus(EngineStatus.ERROR)
          setErrorMessage('Failed to initialize audio output device')
        }
      } catch (error) {
        console.error('🎵 Initialization error:', error)
        setStatus(EngineStatus.ERROR)
        setErrorMessage(error.message)
      }
    }

    initializePrimitives()

    // Cleanup on unmount
    return () => {
      disposed = true
      if (pitchShifterRef.current) {
        console.log('🧹 Cleaning up AudioPitchShifter on component unmount')
        pitchShifterRef.current.destroy()
        pitchShifterRef.current = null
      }
      if (audioCacheRef.current) {
        console.log('🧹 Cleaning up AudioCache on component unmount')
        audioCacheRef.current.clear()
        audioCacheRef.current = null
      }
    }
  }, [])


  // Set lyrics ready checker (for TV page that needs to wait for lyrics)
  const setLyricsReadyChecker = useCallback((callback) => {
    lyricsReadyCallbackRef.current = callback
  }, [])

  const paramsRef = useRef({ vocalVolume, vocalMuted, accompanimentVolume, accompanimentMuted, drumsVolume, drumsMuted, pitchShift });
  useEffect(() => {
    paramsRef.current = { vocalVolume, vocalMuted, accompanimentVolume, accompanimentMuted, drumsVolume, drumsMuted, pitchShift };
  }, [vocalVolume, vocalMuted, accompanimentVolume, accompanimentMuted, drumsVolume, drumsMuted, pitchShift]);

  // Core Loading Logic: Session-Locked & Atomic
  const performLoadingFlow = useCallback(async (song, version) => {
    if (!song || !pitchShifterRef.current || !audioCacheRef.current) return;
    const mediaKey = getSongMediaKey(song)
    if (!mediaKey) {
      setStatus(EngineStatus.ERROR)
      setErrorMessage('Invalid song media key')
      return
    }

    // Guard: Don't start a load if the session has already moved on
    if (currentPlaybackVersionRef.current !== version) return;

    console.log(`🎵 Session ${version} LOADING START: ${song.title}`);
    setStatus(EngineStatus.LOADING);
    setErrorMessage(null);

    try {
      // 1. Prepare engine for new content
      pitchShifterRef.current.cleanupForNewSong();
      if (song.duration) {
        pitchShifterRef.current.setSongDuration(song.duration);
      }
      if (songEndedCallbackRef.current) {
        pitchShifterRef.current.setSongEndedCallback(invokeSongEndedCallback);
      }

      // 2. Load audio (Cache Hit -> Fast, Miss -> Slow)
      let buffers = audioCacheRef.current.get(mediaKey);

      // Post-sync session guard
      if (currentPlaybackVersionRef.current !== version) return;

      if (!buffers) {
        console.log(`🎵 Session ${version} CACHE MISS: Downloading...`);
        buffers = await audioCacheRef.current.loadAndCache(song, pitchShifterRef.current.audioContext);

        // Session Guard after slow network op
        if (currentPlaybackVersionRef.current !== version) {
          console.log(`🎵 Session ${version} ABORTED: Stale network load.`);
          return;
        }
      } else {
        console.log(`🎵 Session ${version} CACHE HIT: Instant load.`);
      }

      // 3. Commit buffers to pitch shifter
      const vocalUrl = getVocalTrackUrl(song)
      const accompanimentUrl = getAccompanimentTrackUrl(song)
      const drumsUrl = getDrumsTrackUrl(song)
      const mainAudioUrl = getMainAudioUrl(song)

      if (buffers.vocal) {
        pitchShifterRef.current.loadAudioFromBuffer(
          buffers.vocal,
          'vocal',
          vocalUrl || mainAudioUrl || undefined
        );
      }
      if (buffers.accompaniment) {
        pitchShifterRef.current.loadAudioFromBuffer(
          buffers.accompaniment,
          'accompaniment',
          accompanimentUrl || undefined
        );
      }
      if (buffers.drums) {
        pitchShifterRef.current.loadAudioFromBuffer(
          buffers.drums,
          'drums',
          drumsUrl || undefined
        );
      }

      // 4. Set initial parameters
      const p = paramsRef.current;
      pitchShifterRef.current.setVocalVolume(p.vocalMuted ? 0 : p.vocalVolume);
      pitchShifterRef.current.setAccompanimentVolume(p.accompanimentMuted ? 0 : p.accompanimentVolume);
      pitchShifterRef.current.setDrumsVolume(p.drumsMuted ? 0 : p.drumsVolume);
      pitchShifterRef.current.setPitchShift(p.pitchShift);

      // Session Guard before committing final "Ready" state
      if (currentPlaybackVersionRef.current !== version) return;

      setStatus(EngineStatus.READY);
      loadedSongRef.current = { mediaKey, version };
      console.log(`🎵 Session ${version} READY: ${song.title}`);

    } catch (error) {
      if (currentPlaybackVersionRef.current !== version) return;

      if (error.message.includes('AudioContext is suspended') || error.message.includes('not allowed to start')) {
        console.log(`🎵 Session ${version} SUSPENDED: Waiting for gesture.`);
        setStatus(EngineStatus.SUSPENDED);
      } else {
        console.error(`🎵 Session ${version} ERROR:`, error);
        setStatus(EngineStatus.ERROR);
        setErrorMessage(error.message || 'Failed to load audio tracks');
      }
    }
  }, [invokeSongEndedCallback]); // Stable across volume/pitch changes

  // Sync: Trigger load when song or version changes
  useEffect(() => {
    if (currentSong) {
      const currentMediaKey = getSongMediaKey(currentSong)
      const songIdentityChanged = loadedSongRef.current.mediaKey !== currentMediaKey

      // Short-circuit: If the song is already loaded for THIS version, skip the loading flow
      // This prevents playback interruption when unrelated store updates refresh the currentSong object
      if (loadedSongRef.current.mediaKey === currentMediaKey &&
        loadedSongRef.current.version === playbackVersion &&
        status !== EngineStatus.ERROR &&
        status !== EngineStatus.IDLE) {
        console.log(`🎵 Session ${playbackVersion} already loaded/loading: ${currentSong.title} - skipping redundant flow`);
        return;
      }

      initialAuthoritySyncDoneRef.current = -1

      // Reset UI progress immediately when transitioning to a different song.
      if (songIdentityChanged) {
        setCurrentTime(0)
      }

      // We trigger performLoadingFlow regardless of current status (if not short-circuited above)
      // This allows recovery from EngineStatus.ERROR when playbackVersion increments or currentSong changes
      performLoadingFlow(currentSong, playbackVersion);
    } else if (!currentSong) {
      setStatus(EngineStatus.IDLE);
      loadedSongRef.current = { mediaKey: null, version: -1 };
      setErrorMessage(null);
      setCurrentTime(0)
      if (pitchShifterRef.current) {
        pitchShifterRef.current.stop();
      }
    }
  }, [currentSong, playbackVersion, performLoadingFlow]);

  // Reconciliation: Sync Intent (isPlaying) with Audio Hardware
  // In authority mode, the TV controls audio directly — skip reconciliation to avoid feedback loops.
  useEffect(() => {
    if (authorityMode === 'authority') return
    // Only attempt to sync if we are READY
    if (status !== EngineStatus.READY || !pitchShifterRef.current) return;

    const isActuallyPlaying = pitchShifterRef.current.isPlaying;

    if (isPlaying && !isActuallyPlaying) {
      // Check if lyrics are ready (for synced lyrics apps)
      const lyricsReady = lyricsReadyCallbackRef.current ? lyricsReadyCallbackRef.current() : true;
      if (!lyricsReady) return;

      console.log(`🎵 Intent Sync [Session ${playbackVersion}]: PLAY`);
      pitchShifterRef.current.play()
        .then(() => setLocalIsPlaying(true))
        .catch(err => {
          if (err.message.includes('suspended') || err.message.includes('not allowed')) {
            setStatus(EngineStatus.SUSPENDED);
          }
        });
    } else if (!isPlaying && isActuallyPlaying) {
      console.log(`🎵 Intent Sync [Session ${playbackVersion}]: PAUSE`);
      pitchShifterRef.current.pause();
      setLocalIsPlaying(false);
    }
  }, [authorityMode, isPlaying, status, playbackVersion]);

  // Authority mode: one-time sync when engine reaches READY for a new song
  useEffect(() => {
    if (authorityMode !== 'authority') return
    if (status !== EngineStatus.READY || !pitchShifterRef.current) return
    if (initialAuthoritySyncDoneRef.current === playbackVersion) return
    initialAuthoritySyncDoneRef.current = playbackVersion

    if (isPlaying) {
      console.log(`🎵 Authority initial sync [Session ${playbackVersion}]: PLAY`)
      pitchShifterRef.current.play()
        .then(() => setLocalIsPlaying(true))
        .catch(err => {
          if (err.message.includes('suspended') || err.message.includes('not allowed')) {
            setStatus(EngineStatus.SUSPENDED);
          }
        });
    }
  }, [authorityMode, status, playbackVersion, isPlaying])

  // Update real-time parameters without reloading
  useEffect(() => {
    if (status === EngineStatus.READY && pitchShifterRef.current) {
      pitchShifterRef.current.setVocalVolume(vocalMuted ? 0 : vocalVolume)
      pitchShifterRef.current.setAccompanimentVolume(accompanimentMuted ? 0 : accompanimentVolume)
      pitchShifterRef.current.setDrumsVolume(drumsMuted ? 0 : drumsVolume)
      pitchShifterRef.current.setPitchShift(pitchShift)
    }
  }, [vocalVolume, vocalMuted, accompanimentVolume, accompanimentMuted, drumsVolume, drumsMuted, pitchShift, status])

  // Monitor for song completion (Backup to onended event)
  useEffect(() => {
    if (localIsPlaying && currentSong && currentSong.duration && currentTime > 0) {
      const timeRemaining = currentSong.duration - currentTime;
      if (timeRemaining <= 0.2 && timeRemaining > 0) {
        if (backupTimerRef.current) clearTimeout(backupTimerRef.current);
        backupTimerRef.current = setTimeout(() => {
          if (localIsPlaying && pitchShifterRef.current && pitchShifterRef.current.isPlaying) {
            const currentTimeNow = pitchShifterRef.current.getCurrentTime();
            if (currentSong.duration - currentTimeNow <= 0.1) {
              console.log('🎵 BACKUP: triggering end of song');
              if (songEndedCallbackRef.current) {
                setLocalIsPlaying(false);
                invokeSongEndedCallback();
              }
            }
          }
        }, 200);
      }
    }
    return () => { if (backupTimerRef.current) clearTimeout(backupTimerRef.current); };
  }, [currentTime, currentSong, localIsPlaying, invokeSongEndedCallback])

  // External Controls
  const handlePlay = async () => {
    if (status === EngineStatus.READY && pitchShifterRef.current) {
      console.log('▶️ Playing via manual trigger (pitch shifting enabled)')
      try {
        await pitchShifterRef.current.play()
        setLocalIsPlaying(true)
      } catch (error) {
        if (error.message.includes('suspended') || error.message.includes('not allowed')) {
          setStatus(EngineStatus.SUSPENDED);
        } else {
          console.error('🎵 Manual play failed:', error);
        }
      }
    }
  }

  const handlePause = () => {
    if (pitchShifterRef.current) {
      console.log('⏸️ Pausing via manual trigger')
      pitchShifterRef.current.pause()
      setLocalIsPlaying(false)
    }
  }

  const handleSeek = async (position, shouldResume = null) => {
    if (status !== EngineStatus.READY || !pitchShifterRef.current) return
    const resumePlayback = shouldResume !== null ? shouldResume : localIsPlaying
    try {
      const vocalUrl = getVocalTrackUrl(currentSong)
      const accompanimentUrl = getAccompanimentTrackUrl(currentSong)
      const drumsUrl = getDrumsTrackUrl(currentSong)

      await pitchShifterRef.current.seek(position, vocalUrl, accompanimentUrl, drumsUrl, resumePlayback)
      setCurrentTime(position)
      setLocalIsPlaying(resumePlayback)
    } catch (error) {
      console.error('🎯 Seek failed:', error)
    }
  }

  const changePitchShiftEngine = useCallback(async (engine) => {
    if (!pitchShifterRef.current) return false

    try {
      return await pitchShifterRef.current.setPitchShiftEngine(engine)
    } catch (error) {
      console.error('🎵 Failed to change pitch shift engine:', error)
      setErrorMessage(error.message || 'Failed to change pitch shift engine')
      return false
    }
  }, [])

  // Handle user gesture to resume AudioContext
  const handleUserGesture = async () => {
    if (pitchShifterRef.current && status === EngineStatus.SUSPENDED) {
      console.log('🎵 User gesture: Resuming context...')
      const resumed = await pitchShifterRef.current.resumeAudioContext()
      if (resumed) {
        console.log('🎵 Context resumed, re-verifying readiness...');
        performLoadingFlow(currentSong, playbackVersion);
      }
    }
  }

  // Set song ended callback (memoized to prevent unnecessary re-renders)
  const setSongEndedCallback = useCallback((callback) => {
    songEndedCallbackRef.current = callback || null

    if (pitchShifterRef.current) {
      pitchShifterRef.current.setSongEndedCallback(songEndedCallbackRef.current
        ? invokeSongEndedCallback
        : null)
    }
  }, [invokeSongEndedCallback])

  // Preload a song into cache (for next/previous songs)
  const preloadSong = useCallback(async (song) => {
    if (!song || !audioCacheRef.current || !pitchShifterRef.current) {
      return
    }
    const mediaKey = getSongMediaKey(song)
    if (!mediaKey) return

    // Don't preload if already cached or loading
    if (audioCacheRef.current.has(mediaKey) || audioCacheRef.current.isLoading(mediaKey)) {
      return
    }

    console.log(`🎵 📦 PRELOADING: ${song.title} (${mediaKey})`)

    try {
      await audioCacheRef.current.loadAndCache(song, pitchShifterRef.current.audioContext)
      console.log(`🎵 📦 ✅ PRELOADED: ${song.title}`)
    } catch (error) {
      console.warn(`🎵 📦 ❌ PRELOAD FAILED: ${song.title}`, error)
    }
  }, [])

  // Invalidate cached song (when queue changes)
  const invalidateCachedSong = useCallback((mediaKey) => {
    if (audioCacheRef.current && mediaKey) {
      audioCacheRef.current.invalidate(mediaKey)
    }
  }, [])

  // Get cache stats for debugging
  const getCacheStats = useCallback(() => {
    return audioCacheRef.current ? audioCacheRef.current.getStats() : null
  }, [])

  return {
    pitchShifterRef,
    status, // New: full status enum
    engineStatus: status, // Alias
    audioEngineReady: status !== EngineStatus.IDLE && status !== EngineStatus.ERROR,
    audioEngineError: status === EngineStatus.ERROR ? errorMessage : null,
    audioTracksLoaded: status === EngineStatus.READY,
    audioLoading: status === EngineStatus.LOADING,
    audioError: status === EngineStatus.ERROR ? errorMessage : null,
    currentTime,
    localIsPlaying,
    needsUserGesture: status === EngineStatus.SUSPENDED,
    handlePlay,
    handlePause,
    handleSeek,
    changePitchShiftEngine,
    setSongEndedCallback,
    handleUserGesture,
    setLyricsReadyChecker,
    getVocalTrackUrl,
    getAccompanimentTrackUrl,
    getMainAudioUrl,
    getDrumsTrackUrl,
    // Cache functions
    preloadSong,
    invalidateCachedSong,
    getCacheStats
  }
}

export default useAudioEngine
